// Diktat-runde — ord-for-ord skrivning med oplæsning.
//
// word-mode (pensum): oplæs hvert ord ved start og efter mellemrum.
// unit-mode (ekstra): oplæs hele enheden én gang; skriv ord for ord.
// Flere ord: hele enheden vises som prikker/udfylde tegn (ikke ét ord ad gangen).

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  KeyboardLayoutId,
  PracticeMode,
  RoundResult,
  Trin,
  UserProfile,
} from '../../types';
import { getDictationAudio } from '../../lib/audio';
import { dictationAudioLocaleForTrin } from '../../lib/dictationAudioLocale';
import { playSuccessChime, primeSuccessChime } from '../../lib/audio/successChime';
import { DICTATION_UNIT_SEPARATOR } from '../../lib/dictationUnits';
import type { PreparedRound } from '../../lib/lessonGenerator';
import { defaultPreferences } from '../../lib/storage';
import { isDictationAudioHintChar } from '../../lib/dictationDisplay';
import { useRoundInput } from '../../lib/useRoundInput';
import {
  activeWordIndex,
  splitWords,
  wordIndexAtCursor,
  type WordSpan,
} from '../../lib/wordSpans';
import Keyboard from '../Keyboard';
import SpaceAdvanceHint from '../SpaceAdvanceHint';

/** Pause after last keystroke so the full sentence stays visible briefly. */
const DICTATION_COMPLETION_HOLD_MS = 1000;

interface DictationRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  user: UserProfile;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  onFinish: (r: RoundResult) => void;
}

function splitDictationUnits(text: string): string[] {
  return text
    .split(DICTATION_UNIT_SEPARATOR)
    .map((s) => s.trim())
    .filter(Boolean);
}

function mergeUnitResults(
  results: RoundResult[],
  meta: {
    roundId: string;
    roundTitle: string;
    mode: PracticeMode;
  },
): RoundResult {
  if (results.length === 0) throw new Error('mergeUnitResults: empty');
  if (results.length === 1) return results[0];

  const totalKeystrokes = results.reduce((s, r) => s + r.totalKeystrokes, 0);
  const correctChars = results.reduce((s, r) => s + r.correctChars, 0);
  const mistakes = results.reduce((s, r) => s + r.mistakes, 0);
  const backspaces = results.reduce((s, r) => s + r.backspaces, 0);
  const durationMs = results.reduce((s, r) => s + r.durationMs, 0);
  const targetLength = results.reduce((s, r) => s + r.targetLength, 0);

  const errorsByKey: Record<string, number> = {};
  for (const r of results) {
    for (const [k, v] of Object.entries(r.errorsByKey)) {
      errorsByKey[k] = (errorsByKey[k] ?? 0) + v;
    }
  }

  const responseTimes: Record<string, number[]> = {};
  for (const r of results) {
    for (const [k, samples] of Object.entries(r.responseTimes)) {
      responseTimes[k] = [...(responseTimes[k] ?? []), ...samples];
    }
  }

  const accuracy =
    totalKeystrokes === 0 ? 1 : correctChars / totalKeystrokes;
  const wpm =
    durationMs <= 0 ? 0 : correctChars / 5 / (durationMs / 60_000);

  return {
    roundId: meta.roundId,
    title: meta.roundTitle,
    mode: meta.mode,
    targetLength,
    durationMs,
    wpm,
    accuracy,
    correctChars,
    totalKeystrokes,
    mistakes,
    backspaces,
    errorsByKey,
    responseTimes,
    completed: results.every((r) => r.completed),
    hintsUsed: results.some((r) => r.hintsUsed),
    presentationKind: 'dictation',
  };
}

export default function DictationRound(props: DictationRoundProps) {
  const presentation = props.round.template.presentation;
  const dictationMode =
    presentation?.kind === 'dictation' ? presentation.dictationMode : 'word';
  const isBulkMode =
    presentation?.kind === 'dictation' &&
    (dictationMode === 'unit' || dictationMode === 'paragraph');

  const units = useMemo(
    () =>
      dictationMode === 'unit'
        ? splitDictationUnits(props.round.targetText)
        : [],
    [dictationMode, props.round.targetText],
  );

  if (dictationMode === 'unit' && units.length > 0) {
    return <DictationUnitSession {...props} units={units} />;
  }

  return (
    <DictationUnitPlayer
      {...props}
      unitText={props.round.targetText}
      unitIndex={0}
      totalUnits={1}
      isUnitMode={isBulkMode}
      dictationMode={dictationMode === 'paragraph' ? 'paragraph' : isBulkMode ? 'unit' : 'word'}
    />
  );
}

function DictationUnitSession({
  round,
  trin,
  keyboardLayout,
  user,
  onFinish,
  units,
  strictCase,
  allowAltLayer,
}: DictationRoundProps & { units: string[] }) {
  const [unitIdx, setUnitIdx] = useState(0);
  const resultsRef = useRef<RoundResult[]>([]);

  const handleUnitFinish = useCallback(
    (r: RoundResult) => {
      resultsRef.current.push(r);
      if (unitIdx < units.length - 1) {
        setUnitIdx((i) => i + 1);
      } else {
        onFinish(
          mergeUnitResults(resultsRef.current, {
            roundId: round.template.id,
            roundTitle: round.template.title,
            mode: round.template.mode,
          }),
        );
      }
    },
    [
      unitIdx,
      units.length,
      onFinish,
      round.template.id,
      round.template.title,
      round.template.mode,
    ],
  );

  return (
    <DictationUnitPlayer
      key={unitIdx}
      round={round}
      trin={trin}
      keyboardLayout={keyboardLayout}
      user={user}
      strictCase={strictCase}
      allowAltLayer={allowAltLayer}
      unitText={units[unitIdx]}
      unitIndex={unitIdx}
      totalUnits={units.length}
      onFinish={handleUnitFinish}
      isUnitMode
      dictationMode="unit"
    />
  );
}

type BulkDictationMode = 'unit' | 'paragraph';

interface DictationUnitPlayerProps extends DictationRoundProps {
  unitText: string;
  unitIndex: number;
  totalUnits: number;
  isUnitMode: boolean;
  dictationMode: BulkDictationMode | 'word';
}

function splitParagraphSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function DictationUnitPlayer({
  round,
  trin,
  keyboardLayout,
  user,
  unitText,
  unitIndex,
  totalUnits,
  onFinish,
  isUnitMode,
  dictationMode,
  strictCase = false,
  allowAltLayer = false,
}: DictationUnitPlayerProps) {
  const targetText = unitText;
  const mode = round.template.mode;
  const prefs = user.preferences ?? defaultPreferences();
  const audioLocale = dictationAudioLocaleForTrin(trin, user);

  const audio = useMemo(() => getDictationAudio(), []);
  const [voicesReady, setVoicesReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [completionHold, setCompletionHold] = useState(false);
  const [showText, setShowText] = useState(false);
  const [awaitingSpace, setAwaitingSpace] = useState(false);
  const plingedAtRef = useRef<number | null>(null);
  const finishRef = useRef<(completed: boolean) => void>(() => {});
  const completionTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void audio.waitForVoices().then(() => {
      if (!cancelled) setVoicesReady(true);
    });
    return () => {
      cancelled = true;
      audio.cancel();
    };
  }, [audio]);

  const audioAvailable =
    prefs.audioEnabled && voicesReady && audio.isAvailable();

  const words = useMemo(() => splitWords(targetText), [targetText]);
  const multiWord = words.length > 1;

  const presentation = round.template.presentation;
  const sentencePauseMs =
    presentation?.kind === 'dictation'
      ? (presentation.sentencePauseMs ?? 600)
      : 600;

  const speakUnit = useCallback(() => {
    if (!audioAvailable || !targetText.trim()) return;
    audio.cancel();
    if (dictationMode === 'paragraph') {
      const sentences = splitParagraphSentences(targetText.trim());
      void (async () => {
        for (let i = 0; i < sentences.length; i++) {
          await audio.speak(sentences[i]!, {
            rate: prefs.dictationRate,
            voiceId: prefs.dictationVoiceId,
            audioLocale,
          });
          if (i < sentences.length - 1) {
            await new Promise((r) => window.setTimeout(r, sentencePauseMs));
          }
        }
      })();
      return;
    }
    void audio.speak(targetText.trim(), {
      rate: prefs.dictationRate,
      voiceId: prefs.dictationVoiceId,
      audioLocale,
    });
  }, [
    audio,
    audioAvailable,
    targetText,
    dictationMode,
    sentencePauseMs,
    prefs.dictationRate,
    prefs.dictationVoiceId,
    audioLocale,
  ]);

  const speakWordAt = useCallback(
    (cursor: number) => {
      if (!audioAvailable || isUnitMode) return;
      const idx = wordIndexAtCursor(words, cursor);
      if (idx < 0) return;
      audio.cancel();
      void audio.speak(words[idx].text, {
        rate: prefs.dictationRate,
        voiceId: prefs.dictationVoiceId,
        audioLocale,
      });
    },
    [
      audio,
      audioAvailable,
      words,
      isUnitMode,
      prefs.dictationRate,
      prefs.dictationVoiceId,
      audioLocale,
    ],
  );

  const acknowledgeWord = useCallback(
    (boundaryCursor: number) => {
      if (plingedAtRef.current === boundaryCursor) return;
      plingedAtRef.current = boundaryCursor;
      if (prefs.audioEnabled) playSuccessChime();
    },
    [prefs.audioEnabled],
  );

  const handleStart = useCallback(() => {
    primeSuccessChime();
    setHasStarted(true);
    if (isUnitMode) speakUnit();
    else speakWordAt(0);
  }, [isUnitMode, speakUnit, speakWordAt]);

  useEffect(() => {
    if (voicesReady && !audioAvailable && !hasStarted) {
      setHasStarted(true);
      setShowText(true);
    }
  }, [voicesReady, audioAvailable, hasStarted]);

  useEffect(() => {
    return () => {
      if (completionTimeoutRef.current != null) {
        window.clearTimeout(completionTimeoutRef.current);
      }
    };
  }, []);

  const input = useRoundInput({
    targetText,
    mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind: 'dictation',
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayout,
    onFinish,
    deferFinishOnComplete: true,
    enabled: hasStarted && !completionHold,
    onAdvance: ({ cursor, char, completed }) => {
      if (char === ' ') {
        if (!multiWord) setAwaitingSpace(false);
        plingedAtRef.current = null;
        if (!completed && !isUnitMode) speakWordAt(cursor);
        return;
      }
      if (completed) {
        acknowledgeWord(cursor);
        setCompletionHold(true);
        completionTimeoutRef.current = window.setTimeout(() => {
          finishRef.current(true);
        }, DICTATION_COMPLETION_HOLD_MS);
        return;
      }
      const needsSpace =
        cursor < targetText.length && targetText[cursor] === ' ';
      if (needsSpace) {
        acknowledgeWord(cursor);
        if (!multiWord) setAwaitingSpace(true);
      } else if (!multiWord) {
        setAwaitingSpace(false);
      }
    },
  });

  finishRef.current = input.finish;

  const currentWordIdx = activeWordIndex(words, input.cursor, awaitingSpace);
  const totalWords = words.length;
  const currentWordNum = currentWordIdx < 0 ? totalWords : currentWordIdx + 1;
  const currentWord = currentWordIdx >= 0 ? words[currentWordIdx] : null;

  const handleRepeat = useCallback(() => {
    if (isUnitMode) {
      speakUnit();
      return;
    }
    if (awaitingSpace && currentWord) {
      speakWordAt(currentWord.start);
      return;
    }
    speakWordAt(input.cursor);
  }, [
    isUnitMode,
    speakUnit,
    awaitingSpace,
    currentWord,
    speakWordAt,
    input.cursor,
  ]);

  const repeatLabel = isUnitMode
    ? dictationMode === 'paragraph'
      ? 'Gentag sætninger'
      : 'Gentag enhed'
    : 'Gentag ord';
  const footerHint =
    dictationMode === 'paragraph'
      ? 'Lyt og skriv. Grå tegn hører ikke med i oplæsningen — taste dem alligevel. Escape afslutter.'
      : multiWord
        ? isUnitMode
          ? 'Lyt og skriv. Grå tegn hører ikke med i oplæsningen — taste dem alligevel. Escape afslutter.'
          : 'Lyt og skriv hele sætningen. Grå tegn hører ikke med i oplæsningen. Escape afslutter.'
        : isUnitMode
          ? 'Lyt og skriv ordet. Grå tegn hører ikke med i oplæsningen. Escape afslutter.'
          : 'Lyt og skriv ordet bogstav for bogstav. Grå tegn = stort eller tegnsætning. Escape afslutter.';

  const progressStat =
    totalUnits > 1
      ? `Enhed ${unitIndex + 1}/${totalUnits} · Ord ${Math.min(currentWordNum, totalWords)}/${totalWords}`
      : `Ord ${Math.min(currentWordNum, totalWords)} / ${totalWords}`;

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat label={totalUnits > 1 ? 'Fremskridt' : 'Ord'} value={progressStat} />
      </div>

      {!hasStarted ? (
        <StartCard
          onStart={handleStart}
          audioAvailable={audioAvailable}
          voicesReady={voicesReady}
          isUnitMode={isUnitMode}
          isParagraphMode={dictationMode === 'paragraph'}
          totalUnits={totalUnits}
          multiWord={multiWord}
        />
      ) : (
        <ListenCard
          currentWord={currentWord}
          progressLabel={progressStat}
          audioAvailable={audioAvailable}
          showText={showText}
          targetText={targetText}
          cursor={input.cursor}
          correctMask={input.correctMask}
          awaitingSpace={!multiWord && awaitingSpace}
          multiWord={multiWord}
          completed={completionHold}
          audioHints={audioAvailable && !showText}
          onRepeat={handleRepeat}
          onToggleShowText={() => setShowText((v) => !v)}
          repeatLabel={repeatLabel}
          isParagraphMode={dictationMode === 'paragraph'}
        />
      )}

      <div className="space-y-2">
        <Keyboard
          layoutId={keyboardLayout}
          nextExpectedKey={
            showText && !completionHold ? input.expectedChar : undefined
          }
          lastPressedKey={input.lastPress?.key}
          lastPressCorrect={input.lastPress?.correct}
          unlockedKeys={trin.unlockedKeys}
          hintLevel={showText && !completionHold ? input.hintLevel : 'none'}
          showFingerDot={
            showText && !completionHold && input.hintLevel !== 'none'
          }
        />
      </div>
      <p className="text-center text-xs text-faint">{footerHint}</p>
    </>
  );
}

interface StartCardProps {
  onStart: () => void;
  audioAvailable: boolean;
  voicesReady: boolean;
  isUnitMode?: boolean;
  isParagraphMode?: boolean;
  totalUnits?: number;
  multiWord?: boolean;
}

function StartCard({
  onStart,
  audioAvailable,
  voicesReady,
  isUnitMode = false,
  isParagraphMode = false,
  totalUnits = 1,
  multiWord = false,
}: StartCardProps) {
  return (
    <div className="surface mx-auto w-full max-w-xl p-8 text-center animate-fade-in-up">
      <SpeakerIcon className="mx-auto h-12 w-12 text-indigo-500" />
      <p className="mt-4 text-lg font-semibold text-strong">
        Sæt fingrene på hjemmerækken
      </p>
      <p className="mt-2 text-sm text-muted">
        {voicesReady && !audioAvailable
          ? 'Ingen dansk stemme tilgængelig på din enhed — vi viser teksten i stedet.'
          : isParagraphMode
            ? 'Du hører hver sætning for sig. Grå bogstaver og tegn vises — de hører ikke med i lyden.'
            : isUnitMode
              ? totalUnits > 1
                ? `Du hører ${totalUnits} enheder. Hver enhed oplæses én gang — udfyld hele sætningen.`
                : 'Hele enheden oplæses én gang — skriv hele sætningen bogstav for bogstav.'
              : multiWord
                ? 'Hele sætningen vises som prikker — grå tegn viser stort og tegnsætning.'
                : 'Lyt og skriv. Grå tegn viser stort eller tegnsætning, som ikke høres i lyden.'}
      </p>
      <button
        type="button"
        autoFocus
        onClick={onStart}
        className="mt-6 rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
      >
        Start oplæsning
      </button>
      <p className="mt-2 text-xs text-faint">
        {isParagraphMode
          ? 'Tryk Gentag sætninger for at høre igen.'
          : isUnitMode
            ? 'Tryk Gentag enhed for at høre igen.'
            : 'Du kan altid trykke på Gentag ord undervejs.'}
      </p>
    </div>
  );
}

interface ListenCardProps {
  currentWord: WordSpan | null;
  progressLabel: string;
  audioAvailable: boolean;
  showText: boolean;
  targetText: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
  awaitingSpace: boolean;
  multiWord: boolean;
  onRepeat: () => void;
  onToggleShowText: () => void;
  repeatLabel: string;
  isParagraphMode?: boolean;
  completed?: boolean;
  audioHints?: boolean;
}

function ListenCard({
  currentWord,
  progressLabel,
  audioAvailable,
  showText,
  targetText,
  cursor,
  correctMask,
  awaitingSpace,
  multiWord,
  onRepeat,
  onToggleShowText,
  repeatLabel,
  isParagraphMode = false,
  completed = false,
  audioHints = false,
}: ListenCardProps) {
  const completionLabel = multiWord
    ? isParagraphMode
      ? 'Sætning færdig'
      : 'Sætning færdig'
    : 'Ord færdig';

  return (
    <div
      className={`surface flex flex-col gap-4 p-6 animate-fade-in-up ${
        completed ? 'ring-2 ring-emerald-400/60 dark:ring-emerald-500/40' : ''
      }`}
    >
      {!audioAvailable && !completed && (
        <div className="rounded-xl bg-amber-100 px-4 py-2 text-center text-xs text-amber-900 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30">
          Ingen dansk stemme fundet. Diktat-runden viser teksten i stedet.
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        {completed ? (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300 animate-fade-in-up">
            <span className="text-xl font-bold">✓</span>
          </div>
        ) : (
          <SpeakerIcon
            className={`h-10 w-10 ${awaitingSpace ? 'text-emerald-500' : 'text-indigo-500'}`}
          />
        )}
        <div className="text-center">
          <p className="label">
            {completed
              ? completionLabel
              : awaitingSpace
                ? 'Ord godkendt'
                : 'Lyt og skriv'}
          </p>
          <p className="mt-1 font-mono text-2xl text-strong">{progressLabel}</p>
        </div>
      </div>

      {multiWord ? (
        <FullTextDictationDisplay
          targetText={targetText}
          cursor={cursor}
          correctMask={correctMask}
          revealText={showText}
          completed={completed}
          audioHints={audioHints}
        />
      ) : currentWord ? (
        <CurrentWordDisplay
          targetText={targetText}
          word={currentWord}
          cursor={cursor}
          correctMask={correctMask}
          awaitingSpace={awaitingSpace}
          revealWord={showText}
          completed={completed}
          audioHints={audioHints}
        />
      ) : (
        <div className="flex h-24 items-center justify-center rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30">
          <p className="text-2xl text-emerald-600 dark:text-emerald-300">✓</p>
        </div>
      )}

      <div
        className={`flex flex-col gap-2 sm:flex-row ${completed ? 'pointer-events-none opacity-50' : ''}`}
      >
        {audioAvailable && (
          <button
            type="button"
            onClick={onRepeat}
            className="flex-1 btn-secondary py-2.5 text-sm font-semibold"
          >
            {repeatLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onToggleShowText}
          className="flex-1 btn-secondary py-2.5 text-sm font-semibold"
        >
          {showText ? 'Skjul tekst' : isParagraphMode ? 'Vis tekst' : 'Vis ord'}
        </button>
      </div>
    </div>
  );
}

interface FullTextDictationDisplayProps {
  targetText: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
  revealText?: boolean;
  completed?: boolean;
  audioHints?: boolean;
}

function FullTextDictationDisplay({
  targetText,
  cursor,
  correctMask,
  revealText = false,
  completed = false,
  audioHints = false,
}: FullTextDictationDisplayProps) {
  const words = useMemo(() => splitWords(targetText), [targetText]);

  const renderChar = (i: number) => {
    const typed = completed || correctMask[i];
    const isCursor = !completed && i === cursor;
    const ch = targetText[i];
    const hintChar = audioHints && isDictationAudioHintChar(ch ?? '');

    if (typed) {
      return (
        <span key={i} className="text-emerald-600 dark:text-emerald-300">
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      );
    }
    if (isCursor) {
      return (
        <span
          key={i}
          className="inline-block min-w-[0.6ch] rounded-sm border-l-2 border-indigo-500 bg-indigo-100 text-zinc-900 dark:bg-indigo-500/10 dark:text-zinc-100"
        >
          {revealText && ch !== ' '
            ? ch
            : hintChar && ch !== ' '
              ? ch
              : '\u00A0'}
        </span>
      );
    }
    if (revealText) {
      return (
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      );
    }
    if (ch === ' ') {
      return (
        <span key={i} className="text-zinc-300 dark:text-zinc-600">
          {'\u00A0'}
        </span>
      );
    }
    if (hintChar) {
      return (
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch}
        </span>
      );
    }
    return (
      <span key={i} className="text-zinc-300 dark:text-zinc-600">
        ·
      </span>
    );
  };

  const segments: ReactNode[] = [];
  for (let wi = 0; wi < words.length; wi++) {
    const word = words[wi];
    if (wi > 0) {
      for (let i = words[wi - 1]!.end; i < word.start; i++) {
        segments.push(renderChar(i));
      }
    }
    segments.push(
      <span
        key={`w-${word.start}`}
        className="inline-flex whitespace-nowrap"
      >
        {Array.from({ length: word.end - word.start }, (_, j) =>
          renderChar(word.start + j),
        )}
      </span>,
    );
  }

  return (
    <div
      className={`flex min-h-[7rem] flex-col items-center justify-center gap-4 rounded-2xl p-6 ring-1 ${
        completed
          ? 'bg-emerald-50 ring-emerald-300 dark:bg-emerald-500/10 dark:ring-emerald-500/40'
          : 'bg-zinc-50 ring-zinc-200 dark:bg-zinc-900/40 dark:ring-white/5'
      }`}
    >
      <p className="max-w-full text-center font-mono text-3xl leading-relaxed tracking-wide sm:text-4xl flex flex-wrap justify-center gap-x-1 gap-y-1">
        {segments.length > 0 ? segments : '\u00A0'}
      </p>
      <p className="text-xs text-faint">
        {completed
          ? 'Sætning færdig'
          : audioHints
            ? 'Grå tegn hører ikke med i oplæsningen — skriv dem alligevel'
            : revealText
              ? 'Sætningen er vist — skriv den bogstav for bogstav'
              : 'Skriv hele sætningen bogstav for bogstav'}
      </p>
    </div>
  );
}

interface CurrentWordDisplayProps {
  targetText: string;
  word: WordSpan;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
  awaitingSpace: boolean;
  revealWord?: boolean;
  completed?: boolean;
  audioHints?: boolean;
}

function CurrentWordDisplay({
  targetText,
  word,
  cursor,
  correctMask,
  awaitingSpace,
  revealWord = false,
  completed = false,
  audioHints = false,
}: CurrentWordDisplayProps) {
  const letters: ReactNode[] = [];

  for (let i = word.start; i < word.end; i++) {
    const typed = completed || correctMask[i];
    const isCursor = !completed && !awaitingSpace && i === cursor;
    const ch = targetText[i];
    const hintChar = audioHints && isDictationAudioHintChar(ch ?? '');

    if (typed) {
      letters.push(
        <span key={i} className="text-emerald-600 dark:text-emerald-300">
          {ch}
        </span>,
      );
    } else if (isCursor) {
      letters.push(
        <span
          key={i}
          className="inline-block min-w-[0.6ch] rounded-sm border-l-2 border-indigo-500 bg-indigo-100 text-zinc-900 dark:bg-indigo-500/10 dark:text-zinc-100"
        >
          {revealWord || hintChar ? ch : '\u00A0'}
        </span>,
      );
    } else if (revealWord) {
      letters.push(
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch}
        </span>,
      );
    } else if (hintChar) {
      letters.push(
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch}
        </span>,
      );
    } else {
      letters.push(
        <span key={i} className="text-zinc-300 dark:text-zinc-600">
          ·
        </span>,
      );
    }
  }

  return (
    <div
      className={`flex min-h-[7rem] flex-col items-center justify-center gap-4 rounded-2xl p-6 ring-1 ${
        completed
          ? 'bg-emerald-50 ring-emerald-300 dark:bg-emerald-500/10 dark:ring-emerald-500/40'
          : 'bg-zinc-50 ring-zinc-200 dark:bg-zinc-900/40 dark:ring-white/5'
      }`}
    >
      <p className="whitespace-pre font-mono text-4xl leading-relaxed tracking-wide">
        {letters.length > 0 ? letters : '\u00A0'}
      </p>
      {completed ? (
        <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
          Ord færdig
        </p>
      ) : awaitingSpace ? (
        <SpaceAdvanceHint />
      ) : (
        <p className="text-xs text-faint">
          {audioHints
            ? 'Grå tegn hører ikke med i oplæsningen — skriv dem alligevel'
            : revealWord
              ? 'Ordet er vist — skriv det'
              : 'Skriv bogstav for bogstav'}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function SpeakerIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
